import { coerceBooleanProperty } from "@angular/cdk/coercion";
import {
  Directive,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Input,
  Output
} from "@angular/core";

import { AbstractButtonGroup } from "./abstract-button-group";
import { ButtonItemInterface } from "./button-item-interface";
import { BUTTON_GROUP } from "./constants";

@Directive()
export abstract class AbstractButtonItem<
  T extends AbstractButtonGroup<ButtonItemInterface>
> {
  @HostBinding("attr.tabindex")
  get tabIndex(): number {
    return this.disabled ? -1 : 0;
  }

  @Input()
  get checked(): boolean {
    return this._checked;
  }

  set checked(value: boolean) {
    value = coerceBooleanProperty(value);
    if (this._checked === value) {
      return;
    }

    this._checked = value;
    this.checkedChange.emit(value);
  }

  private _checked: boolean;

  @Output() checkedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input()
  value: any;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    value = value != null && value !== false;
    if (this._disabled === value) {
      return;
    }
    this._disabled = value;
  }
  private _disabled: boolean;

  get touched(): boolean {
    return this._touched;
  }
  set touched(value: boolean) {
    value = value != null && value !== false;
    if (this._touched === value) {
      return;
    }
    this._touched = value;
    this.touchedChange.emit(value);
  }
  private _touched: boolean;

  @Output() touchedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(@Inject(BUTTON_GROUP) protected parent: T) {
    this.initControlAccessors();
  }

  isParentDisabled(): boolean {
    return this.parent != null && this.parent.disabled;
  }

  select(): void {
    if (!this.checked) {
      this.checked = true;
    }
  }

  @HostListener("click", ["$event"])
  @HostListener("keydown.space", ["$event"])
  onInteraction(event: Event): void {
    if (this.disabled || this.isParentDisabled()) {
      return;
    }

    if (event != null) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.touched = true;
    this.select();
  }

  private initControlAccessors(): void {
    this.checkedChange.subscribe((value) => {
      if (this._onChangeFn == null) {
        return;
      }
      this._onChangeFn(value);
    });

    let touchSubscription: {
      unsubscribe: () => void;
    } = this.touchedChange.subscribe(() => {
      if (this._onTouchedFn != null) {
        this._onTouchedFn();
      }
      touchSubscription.unsubscribe();
    });
  }

  private _onChangeFn?: (value: boolean) => void;
  registerOnChange(fn: any): void {
    this._onChangeFn = fn;
  }

  private _onTouchedFn?: () => void;
  registerOnTouched(fn: any): void {
    this._onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
